import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminPage } from '../features/admin/AdminPage.js';
import { LoginPage } from '../features/auth/LoginPage.js';
import { CalendarPage } from '../features/calendar/CalendarPage.js';
import { RegisterPage } from '../features/auth/RegisterPage.js';
import { GuestsPage } from '../features/guests/GuestsPage.js';
import { HouseDetailPage } from '../features/houses/HouseDetailPage.js';
import { HousesPage } from '../features/houses/HousesPage.js';
import { LandingPage } from '../features/landing/LandingPage.js';
import { PlanPage } from '../features/plan/PlanPage.js';
import { RoomsPage } from '../features/rooms/RoomsPage.js';
import { SojournDetailPage } from '../features/sojourns/SojournDetailPage.js';
import { SojournsPage } from '../features/sojourns/SojournsPage.js';
import { useAuth } from '../shared/auth/useAuth.js';
import { useTranslation } from '../shared/i18n/useLanguage.js';
import { LoadingBlock } from '../shared/ui/index.js';
import { AppLayout } from './AppLayout.js';

export function AppRoutes(): ReactElement {
  const t = useTranslation();
  const { status, canAdminister } = useAuth();

  // Until the stored token has been checked, neither the app nor the sign-in
  // page is the right answer; showing either would flash the wrong screen.
  if (status === 'restoring') {
    return <LoadingBlock label={t.common.restoringSession} />;
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Covers both "never signed in, deep-linked to a protected path"
            and "just signed out" (signing out leaves the URL wherever it
            was, e.g. `/sojourns`, which nothing here recognises) — both
            land on the landing page, which has its own sign-in and
            register CTAs front and centre. Trying to tell those two cases
            apart and send only the second to `/` turned into an unwinnable
            race against this same `<Navigate>`'s own effect; one target,
            always, has no race to lose. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Navigate to="/sojourns" replace />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/houses" element={<HousesPage />} />
        <Route path="/houses/:houseId" element={<HouseDetailPage />} />
        <Route path="/rooms" element={<RoomsPage />} />
        <Route path="/guests" element={<GuestsPage />} />
        <Route path="/sojourns" element={<SojournsPage />} />
        <Route path="/sojourns/:sojournId" element={<SojournDetailPage />} />
        <Route path="/sojourns/:sojournId/plan" element={<PlanPage />} />
        {/* Administration is closed while impersonating, on the server too. */}
        {canAdminister ? <Route path="/admin" element={<AdminPage />} /> : null}
        <Route path="*" element={<Navigate to="/sojourns" replace />} />
      </Route>
    </Routes>
  );
}
